import React, { useState, useEffect } from "react";
import { Button, Form, Input, Upload, message, Tooltip } from "antd";
import { InboxOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { db, storage } from "../../config/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { collection, getDocs, query, where, doc, getDoc, updateDoc } from "firebase/firestore";
import { addDoc } from "firebase/firestore";
import { useMyContext } from "../../context/MyContext";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import getNextInquireNumber from "./GetNextInquiryNumber";
import { getSettings } from "../../lib/setting";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../config/api";
import { FilePdfOutlined, FileExcelOutlined, FileOutlined } from "@ant-design/icons";
import { downloadAttachment } from "../../utils/downloadAttachment";

const EditUploadInvoice = ({ existingData, id }) => {
  const { user } = useMyContext();
  const queryClient = useQueryClient();
  const [fileList, setFileList] = useState(existingData?.uploadFileData || []);
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  /** Existing files the user chose to keep (can remove via UI). Only these + new uploads are sent. */
  const [keptExistingFiles, setKeptExistingFiles] = useState([]);

  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    ["settings"],
    getSettings,
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (existingData) {
      form.setFieldsValue({
        description: existingData.description,
      });
      setFileList(existingData.uploadFileData || []);
    }
  }, [existingData, form]);

  useEffect(() => {
    if (id) {
      fetchQuoteData();
    }
  }, [id]);

  const fetchQuoteData = async () => {
    try {
      const quoteDoc = await getDoc(doc(db, "requests", id));
      if (quoteDoc.exists()) {
        const data = { id: quoteDoc.id, ...quoteDoc.data() };
        setQuoteData(data);
        setKeptExistingFiles(Array.isArray(data.uploadFileData) ? [...data.uploadFileData] : []);
      } else {
        toast.error("Quote not found");
      }
    } catch (error) {
      console.error("Error fetching quote data:", error);
      toast.error("Error fetching quote data");
    }
  };

  const removeKeptFile = (index) => {
    setKeptExistingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleSubmit = async (values) => {
    const newFiles = values.file?.length > 0 ? values.file : [];
    const hasKept = keptExistingFiles.length > 0;
    const hasNew = newFiles.length > 0;
    if (!hasKept && !hasNew) {
      toast.error("Please keep at least one existing file or add at least one new file.");
      return;
    }

    try {
      setLoading(true);
      let newFileData = [];
      if (hasNew) {
        const downloadURLs = await Promise.all(
          newFiles.map(async (file) => {
            const storageRef = ref(storage, `invoiceFiles/${file.name}`);
            await uploadBytes(storageRef, file.originFileObj);
            return getDownloadURL(storageRef);
          })
        );
        newFileData = newFiles.map((a, index) => ({
          name: a.name,
          type: a.type,
          size: a.size,
          uid: a.uid,
          url: downloadURLs[index],
          thumbUrl: downloadURLs[index],
        }));
      }

      const finalUploadFileData = [...keptExistingFiles, ...newFileData];
      await updateFileData(values, finalUploadFileData);

      await api.post("/notifications", {
        title: "Enquiry Request Updated",
        message: `An enquiry request of (${quoteData.inquireNumber}) has been updated by ${quoteData.name}`,
        linkText: "View Updated Request",
        link: `/viewRequest/${id}`,
        role: "admin",
        quoteNumber: quoteData.inquireNumber,
        type: "updated-inquiry"
      });

      queryClient.invalidateQueries(["notifications"]);
      setLoading(false);
      form.resetFields();
      setKeptExistingFiles(finalUploadFileData);
      toast.success("Invoices updated and admin notified successfully!");
    } catch (error) {
      console.error("Error updating files:", error);
      toast.error("Error updating files and notifying admin.");
      setLoading(false);
    }
  };

  const updateFileData = async (values, finalUploadFileData) => {
    try {
      const { file, ...others } = values;
      const requestDoc = doc(db, "requests", id);
      await updateDoc(requestDoc, {
        uploadFileData: finalUploadFileData,
        isInvoiceUpload: true,
        ...others,
      });
    } catch (error) {
      toast.error("Error updating file data:", error);
      console.error("Error updating file data:", error);
      throw error;
    }
  };
  // const saveFileData = async (values, downloadURLs, nextNumber) => {
  //   try {
  //     const { file, ...others } = values;
  //     let fileData = file.map((a, index) => ({
  //       name: a.name,
  //       type: a.type,
  //       size: a.size,
  //       uid: a.uid,
  //       url: downloadURLs[index],
  //       thumbUrl: downloadURLs[index],
  //     }));

  //     const filesCollection = collection(db, "requests");
  //     await addDoc(filesCollection, {
  //       uploadFileData: fileData,
  //       isInvoiceUpload: true,
  //       userId: user?.uid,
  //       inquireNumber: nextNumber,
  //       name: user?.name,
  //       requestStatus: "Unquoted",
  //       creationDate: new Date().toISOString(),
  //       ...others,
  //     });

  //     toast.success("File data saved successfully.");
  //   } catch (error) {
  //     toast.error("Error saving file data:", error);
  //     console.error("Error saving file data:", error);
  //   }
  // };

  const beforeUpload = (file) => {
    const isFileTypeValid =
      file.type === "application/pdf" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "text/csv" ||
      file.name.toLowerCase().endsWith(".csv");
    if (!isFileTypeValid) {
      toast.error("You can only upload PDF, Excel, or CSV files!");
    }
    return false;
  };

  const validateFileType = (_, fileList) => {
    if (fileList && fileList.length > 0) {
      const invalidFiles = fileList.filter(file => !(
        file.type === "application/pdf" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv" ||
        file.name.toLowerCase().endsWith(".csv")
      ));
      if (invalidFiles.length > 0) {
        return Promise.reject("You can only upload PDF, Excel, or CSV files!");
      }
    }
    return Promise.resolve();
  };

  const getFileIcon = (file) => {
    // Implement this function to return the appropriate icon based on file type
    // For example:
    if (file.type === "application/pdf") return <FilePdfOutlined />;
    if (file.type.includes("spreadsheet")) return <FileExcelOutlined />;
    return <FileOutlined />;
  };

  return (
    <div style={{ margin: 30 }}>
      <Form layout="vertical" onFinish={handleSubmit} form={form}>
        <Form.Item
          label="Invoice Files (add new optional)"
          name="file"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              validator: (_, fileList) => {
                const list = fileList || [];
                if (keptExistingFiles.length > 0) return Promise.resolve();
                if (list.length === 0) return Promise.reject("Keep at least one existing file below or add at least one new file.");
                return validateFileType(_, list);
              },
            },
          ]}
        >
          <Upload.Dragger
            beforeUpload={beforeUpload}
            name="files"
            multiple={true}
            accept=".pdf,.xlsx,.xls,.csv"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Supporting Documents (E.G. Commercial Invoice, Packing List, Datasheets)
            </p>
            <p className="ant-upload-hint">
              Only PDF, Excel and CSV files are allowed
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please Enter Description" }]}
        >
          <Input.TextArea autoSize={{ minRows: 4 }} showCount />
        </Form.Item>
        <Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            loading={loading}
            className={id ? "bg-cus-blue rounded-md text-white p-2 text-sm md:w-[341px] w-[180px]" : ""}
          >
            {id ? (loading ? "Loading..." : " Update Requested quotation") : "SEND"}
          </Button>
        </Form.Item>
      </Form>

      {quoteData && quoteData.isInvoiceUpload && keptExistingFiles.length > 0 && (
        <div className="mt-4">
          <h3 className="font-medium mb-2">Previously uploaded files (uncheck to remove from request)</h3>
          <p className="text-sm text-gray-500 mb-2">Only files you keep below will be sent. Click the X to remove a file from this request.</p>
          <div className="flex flex-wrap gap-2">
            {keptExistingFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-1 border border-gray-200 rounded-md pl-2 pr-1 py-1 bg-gray-50"
              >
                <Tooltip title={file.name}>
                  <Button
                    type="text"
                    size="small"
                    icon={getFileIcon(file)}
                    onClick={() => {
                      downloadAttachment(file.url, file.name).catch(() => {
                        toast.error("Unable to download file. Please try again.");
                      });
                    }}
                    className="!p-1"
                  />
                </Tooltip>
                <span className="text-sm max-w-[120px] truncate" title={file.name}>{file.name}</span>
                <Tooltip title="Remove from request">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => removeKeptFile(index)}
                    className="!p-1"
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditUploadInvoice;